import { ColDef } from 'ag-grid-community';

import { buildColDef, ColType } from 'app/shared/utils/ag-grid.col-def.utils';
import { sidemenuItemTemplate } from 'v2/modules/shared/components/ag-grid-sidemenu/ag-grid-sidemenu.component';
import {
  IAgGridSideMenuItem,
  IAgGridSideMenuItemConfig,
} from 'v2/modules/shared/interfaces/ag-grid/ag-grid-sidemenu.interface';
import {
  IIdName,
  SortDirections,
} from 'v2/modules/shared/interfaces/common.interface';

import { StockSelectAllAgGridComponent } from '../components/stock-select-all-ag-grid/stock-select-all-ag-grid.component';
import { EStockStatusValue } from '../interfaces/stock.interface';

import { StockConfig } from './stock.config';

const relations :string[] = [];

const onDemandServerSideRelations :any = {};

const gridOptions :any = {
  groupDefaultExpanded: 0,
  suppressCopyRowsToClipboard: true,
  sideBar: {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: {
          suppressValues: true,
          suppressPivots: true,
          suppressPivotMode: true,
          suppressRowGroups: false,
        },
      },
    ],
    defaultToolPanel: '',
  },
};

const groupByColumns :any[] = [
  {
    headerName: 'Warehouse Group',
    field: 'warehouseGroupLabel',
    label: 'Warehouse Group',
  },
  {
    headerName: 'SKU',
    field: 'sku',
    label: 'SKU',
  },
  {
    headerName: 'Preferred Supplier',
    field: 'preferredSupplierName',
    label: 'Preferred Supplier',
  },
];

const defaultColDef :any = {
  sortable: true,
  filter: true,
  floatingFilter: true,
  resizable: true,
  enableRowGroup: false,
  enablePivot: false,
  filterParams: {
    debounceMs: 1000,
    filterOptions: ['equals'],
    defaultOption: 'equals',
    maxNumConditions: 1,
  },
};

const fields = {
  CHECKBOX_SELECT_ALL: 'checkboxSelectAll',
  WAREHOUSE_GROUP: 'warehouseGroupId',
  SKU: 'internalSku',
  PRODUCT_NAME: 'productName',
  ACTIONS: 'actions',
  INCOMING_QTY: 'incomingQty',
  ONHAND_QTY: 'onHandQty',
  PENDING_QTY: 'pendingQty',
  AVAILABLE_QTY: 'availableQty',
  STOCK_STATUS: 'stockStatus',
  CATEGORY: 'categoryName',
  SUB_CATEGORY: 'subCategoryName',
  PRODUCT_TYPE: 'productType',
  PREFERRED_SUPPLIER: 'preferredSupplier',
};

const pageSizeOptions :number[] = [5, 10, 25, 100];

const defaultQueryParams :{ [key :string] :any } = {
  relations: relations.join('|'),
  sortField: fields.SKU,
  sortDirection: SortDirections.DESC,
  limit: '100',
  page: 1,
  stockStatus: '',
};

const defaultGroupQueryParams = {
  limit: '100',
  page: '1',
};

const stockAgTableColumnDefs :ColDef[] = [
  buildColDef(ColType.CHECKBOX, {
    headerName: '',
    headerCheckboxSelection: false,
    headerCheckboxSelectionFilteredOnly: false,
    headerComponent: StockSelectAllAgGridComponent,
  }),
  buildColDef(
    ColType.DEFAULT,
    {
      headerName: 'SKU',
      field: fields.SKU,
    },
    {
      routerLinkFormatter: ({ data }) =>
        data?.rfqId ? `/inventory/${data.rfqId}/view` : '',
    },
  ),
  buildColDef(
    ColType.DEFAULT,
    {
      headerName: 'Product Name',
      field: fields.PRODUCT_NAME,
      width: 300,
    },
    {
      routerLinkFormatter: ({ data }) =>
        data?.rfqId ? `/inventory/${data.rfqId}/view` : '',
    },
  ),
  {
    headerName: 'Warehouse Group',
    field: fields.WAREHOUSE_GROUP,
    sortable: true,
    filter: 'agSetColumnFilter',
    filterParams: {
      debounceMs: 1000,
      values: [],
    },
    valueGetter: (params :any) => params?.data?.warehouseGroupId || '',
    valueFormatter: (params :any) => params?.data?.warehouseGroupLabel || '',
  },
  buildColDef(ColType.QUANTITY, {
    headerName: 'Incoming Quantity',
    field: fields.INCOMING_QTY,
    width: 130,
  }),
  buildColDef(ColType.QUANTITY, {
    headerName: 'On-hand Quantity',
    field: fields.ONHAND_QTY,
    width: 130,
  }),
  buildColDef(ColType.QUANTITY, {
    headerName: 'Pending Quantity',
    field: fields.PENDING_QTY,
    width: 130,
  }),
  buildColDef(ColType.QUANTITY, {
    headerName: 'Available Quantity',
    field: fields.AVAILABLE_QTY,
    width: 130,
  }),
  {
    headerName: 'Preferred Supplier',
    field: fields.PREFERRED_SUPPLIER,
    sortable: true,
    filter: 'agTextColumnFilter',
    filterParams: {
      debounceMs: 1000,
      filterOptions: ['contains'],
      defaultOption: 'contains',
      maxNumConditions: 1,
    },
    valueGetter: (params :any) => params?.data?.preferredSupplierName || '',
    valueFormatter: (params :any) => params?.data?.preferredSupplierName || '',
  },
  {
    headerName: 'Stock Status',
    field: fields.STOCK_STATUS,
    hide: true,
    sortable: true,
    filter: false,
    valueFormatter: (params :any) => {
      const stockStatus = StockConfig.stockStatus.find(
        (row :IIdName) => row.id === params?.data?.stockStatus || '',
      );

      return stockStatus?.name || '';
    },
  },
  {
    headerName: 'Category',
    field: fields.CATEGORY,
    sortable: true,
    hide: true,
    filter: 'agTextColumnFilter',
    filterParams: {
      debounceMs: 1000,
      filterOptions: ['contains'],
      defaultOption: 'contains',
      maxNumConditions: 1,
    },
  },
  {
    headerName: 'Sub Category',
    field: fields.SUB_CATEGORY,
    sortable: true,
    hide: true,
    filter: 'agTextColumnFilter',
    filterParams: {
      debounceMs: 1000,
      filterOptions: ['contains'],
      defaultOption: 'contains',
      maxNumConditions: 1,
    },
  },
  {
    headerName: 'Product Type',
    field: fields.PRODUCT_TYPE,
    hide: true,
    sortable: true,
    filter: 'agSetColumnFilter',
    filterParams: {
      debounceMs: 1000,
      values: StockConfig.productTypes.map((status) => status.id),
      valueFormatter: (data :any) => {
        const productType = StockConfig.productTypes.find(
          (row :IIdName) => row.id === data.value,
        );

        return productType?.name;
      },
      newRowsAction: 'keep',
    },
    valueGetter: (params :any) => params?.data?.productType,
    valueFormatter: (params :any) => {
      const productType = StockConfig.productTypes.find(
        (row :IIdName) => row.id === params?.data?.productType,
      );

      return productType?.name || '';
    },
  },
];

const sideMenuConfig :IAgGridSideMenuItemConfig = {
  renderHtml: (item :IAgGridSideMenuItem) => {
    const html = '';
    let itemTextColor = '';
    let itemBadgeColor = '';
    const statusCountHtml = '';

    switch (item.name) {
      case EStockStatusValue.REORDER_LEVEL:
        itemTextColor = 'text-yellow-500';
        itemBadgeColor = 'bg-yellow-200 text-yellow-800';
        break;

      case EStockStatusValue.IN_STOCK:
        itemTextColor = 'text-green-500';
        itemBadgeColor = 'bg-green-200 text-green-800';
        break;

      case EStockStatusValue.MINIMUM_STOCK:
        itemTextColor = 'text-red-500';
        itemBadgeColor = 'bg-red-200 text-red-800';
        break;

      default:
        itemTextColor = 'text-cloud-500';
        itemBadgeColor = 'bg-cloud-200 text-cloud-800';
        break;
    }

    return sidemenuItemTemplate({
      badgeColorClass: itemBadgeColor,
      badgeLabel: item.data?.statusCount,
      colorClass: itemTextColor,
      label: item.label,
    });
  },
};

export const StockAgTableConfig = {
  relations,
  onDemandServerSideRelations,
  gridOptions,
  defaultColDef,
  pageSizeOptions,
  fields,
  defaultQueryParams,
  groupByColumns,
  defaultGroupQueryParams,
  stockAgTableColumnDefs,
  sideMenuConfig,
};
